import type { HTMLAttributes, ReactNode } from 'react';
import { cx } from './cx.js';

/**
 * What a settings row says: its name, and one quiet line under it.
 *
 * This is the half of a row that is not the control. `Switch`, `Choice` and
 * `Level` each wrap a `<label>` around a different control, and all three open
 * with the same three elements — a column, the name, and an optional hint in
 * smaller muted type. Extracted at three copies, which is the cheapest moment:
 * two of them were byte-identical and the third had simply not grown a hint
 * yet, so there was nothing to reconcile. Waiting for a fourth is how the hint
 * ends up at different sizes in different rows, and nobody sees it
 * because no two settings screens are ever open at once.
 *
 * `min-w-0` is in here on purpose and is the part that gets forgotten. A flex
 * child defaults to `min-width: auto`, so a long name pushes the control off
 * the row instead of wrapping or truncating. `LobbyScreen`'s seat row is the
 * proof: it wrote this stack by hand, left out both `min-w-0` and `flex-col`,
 * and its `truncate` did nothing at all for it.
 *
 * **How wide it is belongs to the caller.** There is no `flex-1` in the base
 * string, and that is deliberate rather than an omission: a row whose control
 * is a 3rem switch wants `flex-1`, and a row whose slider wraps to its own line
 * wants `grow basis-3/5`. Baking one in would mean the other passed a second
 * `flex` utility, and which of two utilities from the same family wins is
 * decided by the order Tailwind emits them, not by the order they appear in the
 * attribute. `Cover` documents the same trap for `justify-*`.
 *
 * `MenuTile` keeps its own label stack. Its display-weight name differs from
 * this settings label. Both hints read at the default size (Mitch,
 * 2026-09-21); the shared size does not change their markup ownership.
 */

export interface FieldLabelProps extends Omit<
  HTMLAttributes<HTMLSpanElement>,
  'className' | 'style' | 'children'
> {
  /** One line under the name, for a setting whose name is not the whole story. */
  readonly hint?: string | undefined;
  /** The hint's size. `sm` is for a tight panel, like `ActionMenu`'s dropdown. */
  readonly hintSize?: 'base' | 'sm' | undefined;
  /**
   * How much of the row the label takes, and any other layout. Required in
   * practice: the base string sets no flex sizing, for the reason above.
   */
  readonly className?: string | undefined;
  /** The name of the setting. Already translated. */
  readonly children: ReactNode;
}

const STACK = 'flex min-w-0 flex-col gap-0.5';

/*
 * The two weights live on the two spans, one class each, and not on the rows
 * that wrap them. A setting's name is UI text and reads at 700; its hint is
 * body text and reads at 400. The hint says `font-normal` out loud because a
 * `Switch` row is a real `<label>`, and `base.css` sets labels to 500. Writing a
 * weight on the row instead would put two
 * font-weight utilities on one element, and which of them wins is decided by
 * the order Tailwind emits the group rather than by anyone's intent — the trap
 * `buttonLook` documents, which had every primary button in the product
 * rendering at the wrong weight for months.
 */
const NAME = 'leading-snug font-bold';

const HINT = 'leading-normal font-normal text-pretty text-gray-700';

const HINT_SIZE = { base: '', sm: 'text-sm' } as const;

export function FieldLabel({
  hint,
  hintSize = 'base',
  className,
  children,
  ...rest
}: FieldLabelProps): React.JSX.Element {
  return (
    <span className={cx(STACK, className)} {...rest}>
      <span className={NAME}>{children}</span>
      {hint !== undefined && <span className={cx(HINT, HINT_SIZE[hintSize])}>{hint}</span>}
    </span>
  );
}
